import React, { useEffect, useState } from 'react'
import { useMutation } from '@apollo/client'
import { Box, Button, CircularProgress } from '@material-ui/core'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import pick from 'lodash/pick'
import validator from '@rjsf/validator-ajv8'
import { IChangeEvent } from '@rjsf/core'
import {
  createSchemaUtils,
  NAME_KEY,
  RJSF_ADDITIONAL_PROPERTIES_FLAG,
} from '@rjsf/utils'
import { ReactJsonSchemaForm } from '@flint/rjsf'
import { useTranslation } from '@flint/locales'
import toast from 'react-hot-toast'
import { AddPhotoToGallery } from 'Rjsf'
import { ORGANIZATION_ID } from 'shared'
import { SimpleProjectService } from 'services/simpleProject.service'
import { compressImg, logError } from 'utils/general.utils'
import { RASED_QA_UPDATE_RECORD } from 'global/graphql/SimpleProjectQA'
import ReadOnlyProperties from 'components/ReadOnlyProperties'

// Replicates RJSF's internal omitExtraData algorithm using exported @rjsf/utils primitives.
// Walks the PathSchema tree RJSF builds and collects only the field paths the form actually
// renders, then picks those paths from formData via lodash.
function collectFieldPaths(
  pathSchema: any,
  formData: any,
  acc: Array<string | string[]> = [],
  paths: string[][] = [[]]
): Array<string | string[]> {
  Object.keys(pathSchema).forEach((key) => {
    const node = pathSchema[key]
    if (typeof node !== 'object') {
      if (key === NAME_KEY && node !== '') {
        paths.forEach((path) => {
          const val = get(formData, path)
          if (
            typeof val !== 'object' ||
            isEmpty(val) ||
            (Array.isArray(val) && val.every((v) => typeof v !== 'object'))
          ) {
            acc.push(path)
          }
        })
      }
      return
    }
    const newPaths = paths.map((p) => [...p, key])
    if (node[RJSF_ADDITIONAL_PROPERTIES_FLAG] && node[NAME_KEY] !== '') {
      acc.push(node[NAME_KEY])
    } else {
      collectFieldPaths(node, formData, acc, newPaths)
    }
  })
  return acc
}

function omitExtraData(
  schema: any,
  data: Record<string, any>
): Record<string, any> {
  const schemaUtils = createSchemaUtils(validator, schema)
  const resolvedSchema = schemaUtils.retrieveSchema(schema, data)
  const pathSchema = schemaUtils.toPathSchema(resolvedSchema, '', data)
  const fieldPaths = collectFieldPaths(pathSchema, data)
  const result = pick(data, fieldPaths as any[])
  return Array.isArray(data) ? (Object.values(result) as any) : result
}

interface QARecord {
  id: number
  data: Record<string, any>
  geometry: any
  sourceProperties: any
  mapData: any
}

interface QARecordPanelProps {
  record: QARecord
  orderId: number
  jsonSchema: any
  onClose: () => void
  onRecordUpdated: () => void
}

export const QARecordPanel = ({
  record,
  orderId,
  jsonSchema,
  onClose,
  onRecordUpdated,
}: QARecordPanelProps) => {
  const { t } = useTranslation()

  const parseData = (data: any): Record<string, any> => {
    if (!data) return {}
    let parsed: any = data
    if (typeof data === 'string') {
      try {
        parsed = JSON.parse(data)
      } catch {
        return {}
      }
    }
    while (
      parsed &&
      typeof parsed === 'object' &&
      !Array.isArray(parsed) &&
      'rased' in parsed
    ) {
      parsed = parsed.rased
    }
    return parsed ?? {}
  }

  const [formData, setFormData] = useState<Record<string, any>>(
    parseData(record.data)
  )

  useEffect(() => {
    setFormData(parseData(record.data))
  }, [record.id])

  const [updateRecord, { loading }] = useMutation(RASED_QA_UPDATE_RECORD)

  const rawSchema =
    typeof jsonSchema === 'string' ? JSON.parse(jsonSchema) : jsonSchema
  const parsedSchema = rawSchema?.rased?.form ?? rawSchema
  const parsedUiSchema = rawSchema?.rased?.webUISchema ?? {}
  const configuration = rawSchema?.rased?.configuration

  const handleSubmit = async (data: IChangeEvent<any>) => {
    const submittedData = omitExtraData(
      parsedSchema,
      data?.formData ?? formData
    )
    try {
      await updateRecord({
        variables: {
          recordInput: {
            orderId,
            recordId: record.id,
            formData: JSON.stringify(submittedData),
          },
        },
      })
      toast.success(t('saved successfully'), { duration: 1500 })
      onRecordUpdated()
    } catch (err) {
      logError('QARecordPanel', `Failed to update record: ${err}`)
      toast.error(t('offer not edited'), { duration: 1500 })
    }
  }

  const onError = (error: any) => {
    toast.error(t('form_error_msg'), { duration: 1500 })
    logError('QARecordPanel', error)
  }

  const onFileChange = async (data: Array<{ file: File }> | { file: File }) => {
    const service = new SimpleProjectService()
    try {
      if (Array.isArray(data)) {
        const promises = data.map(async ({ file }) => {
          const compressedFile = await compressImg(file)
          return service.uploadViaSignedUrl(
            compressedFile,
            orderId,
            ORGANIZATION_ID,
            service.uploadFile
          )
        })
        return await Promise.all(promises)
      } else {
        const compressedFile = await compressImg(data?.file)
        return service.uploadViaSignedUrl(
          compressedFile,
          orderId,
          ORGANIZATION_ID,
          service.uploadFile
        )
      }
    } catch (error) {
      logError('QARecordPanel', error, ['onFileChange'])
    }
  }

  if (!parsedSchema) return null

  return (
    <Box p={2}>
      <ReadOnlyProperties
        configuration={configuration}
        selectedRecord={record}
      />
      <ReactJsonSchemaForm
        validator={validator}
        schema={parsedSchema}
        uiSchema={parsedUiSchema}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
        onError={onError}
        disabled={loading}
        liveValidate={false}
        showErrorList={false}
        omitExtraData
        liveOmit
        noHtml5Validate
        experimental_defaultFormStateBehavior={{ allOf: 'populateDefaults' }}
        widgets={{ AddPhotoToGallery }}
        formContext={{ onFileChange }}
        transformErrors={(errors) =>
          errors?.map((err) => {
            if (err.name === 'required' || err.name === 'minItems') {
              return { ...err, message: 'حقل مطلوب' }
            }
            if (err.name === 'enum') {
              return { ...err, message: 'يرجى الإختيار من القيم الموجودة' }
            }
            return err
          })
        }
      >
        <Box mt={2} display="flex" style={{ gap: 8 }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="small"
            disabled={loading}
            fullWidth
          >
            {loading ? <CircularProgress size={16} /> : t('save')}
          </Button>
          <Button
            variant="contained"
            color="default"
            size="small"
            disabled={loading}
            fullWidth
            onClick={onClose}
          >
            {t('cancel')}
          </Button>
        </Box>
      </ReactJsonSchemaForm>
    </Box>
  )
}
